import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../authentication/AuthContext';
import Header from '../components/Header';
import { useDeleteTask, useTaskById, useUpdateTask } from './hooks/useTasks';
import { useProject, useProjects } from '../project/hooks/useProjects';
import AddTaskModal from './components/AddTaskModal';
import {
    ArrowLeft,
    Calendar,
    Clock,
    Flag,
    FolderOpen,
    CheckCircle2,
    Circle,
    Trash2,
    AlertTriangle,
    Pencil,
} from 'lucide-react';
import {authFetch} from "../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-En', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

function getDeadlineStatus(deadlineTs) {
    if (!deadlineTs) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const dl = new Date(deadlineTs);
    dl.setHours(0, 0, 0, 0);
    const diff = Math.round((dl - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { label: `${Math.abs(diff)} days due`, color: 'text-red-400 bg-red-400/10 border-red-400/20' };
    if (diff === -1) return { label: 'Yesterday was last day', color: 'text-yellow-400 bg-yellow-400/10 border-red-400/20' };
    if (diff === 0) return { label: 'Today is last day', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' };
    if (diff === 1) return { label: 'Tomorrow is last day son gün', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20' };
    return { label: `${diff} days remained`, color: 'text-green-400 bg-green-400/10 border-green-400/20' };
}

function getPriorityFlagClass(priority) {
    if (priority === 'HIGH') return 'text-red-400';
    if (priority === 'MEDIUM') return 'text-yellow-400';
    if (priority === 'LOW') return 'text-blue-400';
    return 'text-gray-500';
}

export const TaskDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authToken } = useAuth();
    const { data: task, isLoading, refetch } = useTaskById(authToken, id);
    const { data: project } = useProject(authToken, task?.projectId);
    const { data: projects } = useProjects(authToken);
    const { mutate: deleteTask } = useDeleteTask(authToken);
    const { mutate: updateTask, isLoading: isUpdating } = useUpdateTask(authToken);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const handleToggleStatus = async () => {
        const newStatus = task.taskStatus === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
        await authFetch(`${API_URL}/api/task/${id}?taskStatus=${newStatus}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                authorization: `Bearer ${authToken}`,
            },
        });
        refetch();
    };

    const handleDelete = () => {
        deleteTask(id, {
            onSuccess: () => navigate('/task'),
        });
    };

    const handleEditSubmit = (inputValue) => {
        updateTask(
            { id, task: inputValue },
            {
                onSuccess: () => {
                    setShowEditModal(false);
                    refetch();
                },
            }
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <div className="text-gray-400 text-sm animate-pulse">Loading...</div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <div className="text-gray-500 text-sm">Görev bulunamadı.</div>
            </div>
        );
    }

    const isCompleted = task.taskStatus === 'COMPLETED';
    const deadlineStatus = getDeadlineStatus(task.deadlineDate);
    const priority = task.priority ?? task.taskPriority;

    return (
        <div className="min-h-screen bg-[#12131a]">
            <Header />
            <div className="max-w-2xl mx-auto px-6 pt-24 pb-16">

                {/* Back */}
                <button
                    onClick={() => navigate('/task')}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition mb-8"
                >
                    <ArrowLeft size={16} />
                    Back to tasks
                </button>

                {/* Status badge + title */}
                <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-6 mb-4 shadow-lg">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <button
                                onClick={handleToggleStatus}
                                className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-indigo-400 transition"
                                title={isCompleted ? 'Mark as pending' : 'Mark as completed'}
                            >
                                {isCompleted
                                    ? <CheckCircle2 size={24} className="text-indigo-400" />
                                    : <Circle size={24} />}
                            </button>
                            <h1 className={`text-xl font-semibold leading-snug break-words ${isCompleted ? 'line-through text-gray-500' : 'text-gray-100'}`}>
                                {task.text}
                            </h1>
                        </div>
                        <span className={`flex-shrink-0 text-xs font-medium px-3 py-1 rounded-full border ${
                            isCompleted
                                ? 'text-green-400 bg-green-400/10 border-green-400/20'
                                : 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20'
                        }`}>
                            {isCompleted ? 'Completed' : 'Pending'}
                        </span>
                    </div>
                </div>

                {/* Deadline status banner */}
                {deadlineStatus && !isCompleted && (
                    <div className={`flex items-center gap-2 text-sm font-medium px-4 py-3 rounded-xl border mb-4 ${deadlineStatus.color}`}>
                        <AlertTriangle size={15} />
                        {deadlineStatus.label}
                    </div>
                )}

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                            <Calendar size={12} />
                            Deadline
                        </div>
                        <p className="text-gray-200 text-sm font-medium">
                            {formatDate(task.deadlineDate)}
                        </p>
                    </div>

                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                            <Clock size={12} />
                            Created at
                        </div>
                        <p className="text-gray-200 text-sm font-medium">
                            {formatDate(task.createdDate)}
                        </p>
                    </div>

                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                            <Flag size={12} className={getPriorityFlagClass(priority)} />
                            Priority
                        </div>
                        <p className="text-gray-200 text-sm font-medium">
                            {priority ? `${priority.charAt(0)}${priority.slice(1).toLowerCase()}` : '—'}
                        </p>
                    </div>

                    {project?.name && task?.projectId ? (
                        <button
                            type="button"
                            onClick={() => navigate(`/project/${task.projectId}`)}
                            className="col-span-2 text-left bg-[#1E1F25] border border-[#2A2B32] rounded-xl p-4 transition hover:border-indigo-500/40 hover:bg-[#242633]"
                        >
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                                <FolderOpen size={12} />
                                Project
                            </div>
                            <p className="text-gray-200 text-sm font-medium">
                                {project.name}
                            </p>
                        </button>
                    ) : null}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowEditModal(true)}
                        disabled={isUpdating}
                        className="px-5 py-3 rounded-xl border border-gray-700 text-gray-300 hover:border-indigo-500/50 hover:text-indigo-300 hover:bg-indigo-500/5 text-sm font-medium transition flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <Pencil size={15} />
                        Edit
                    </button>

                    <button
                        onClick={handleToggleStatus}
                        className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition"
                    >
                        {isCompleted ? 'Mark as pending' : 'Mark as completed'}
                    </button>

                    {!confirmDelete ? (
                        <button
                            onClick={() => setConfirmDelete(true)}
                            className="px-5 py-3 rounded-xl border border-gray-700 text-gray-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/5 text-sm font-medium transition flex items-center gap-2"
                        >
                            <Trash2 size={15} />
                            Delete
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={handleDelete}
                                className="px-4 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition flex items-center gap-1.5"
                            >
                                <Trash2 size={14} />
                                Evet, sil
                            </button>
                            <button
                                onClick={() => setConfirmDelete(false)}
                                className="px-4 py-3 rounded-xl border border-gray-700 text-gray-400 hover:bg-gray-800 text-sm font-medium transition"
                            >
                                İptal
                            </button>
                        </div>
                    )}
                </div>

                {showEditModal && (
                    <AddTaskModal
                        onClose={() => setShowEditModal(false)}
                        onSubmit={handleEditSubmit}
                        projects={projects ?? []}
                        isEditMode
                        initialData={{
                            title: task.text,
                            deadline: task.deadlineDate,
                            repeat: task.repeat,
                            priority: task.priority ?? task.taskPriority ?? '',
                            projectId: task.projectId,
                        }}
                    />
                )}
            </div>
        </div>
    );
};
